import React, { useState, useEffect } from 'react';
import SharedCard   from './SharedCard';
import SharedSelect from './SharedSelect';
import SharedInput  from './SharedInput';
import SharedButton from './SharedButton';
import { generateUniqueId } from '../utils/storage';

const mxn = n => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

const calcDisc = (item) => {
  const line = Number(item.price) * item.quantity;
  return item.discountType === '%'
    ? line * (Math.min(100, Math.max(0, Number(item.discount || 0))) / 100)
    : Math.min(line, Math.max(0, Number(item.discount || 0)));
};

const POSComponent = () => {
  const [products,              setProducts]              = useState([]);
  const [students,              setStudents]              = useState([]);
  const [selectedStudentId,     setSelectedStudentId]     = useState('');
  const [selectedProductForAdd, setSelectedProductForAdd] = useState(null);
  const [selectedProducts,      setSelectedProducts]      = useState([]);
  const [quantity,              setQuantity]              = useState(1);
  const [selectedColor,         setSelectedColor]         = useState('');
  const [selectedSize,          setSelectedSize]          = useState('');
  const [total,                 setTotal]                 = useState(0);
  const [lastReceiptData,       setLastReceiptData]       = useState(null);
  const [processing,            setProcessing]            = useState(false);
  const [categoryFilter,        setCategoryFilter]        = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [prodRes, stuRes] = await Promise.all([
          fetch('/api/list_products.php?full=1', { credentials: 'include' }),
          fetch('/api/list_students.php', { credentials: 'include' }),
        ]);
        const [prodData, stuData] = await Promise.all([prodRes.json(), stuRes.json()]);
        setProducts(Array.isArray(prodData) ? prodData : []);
        setStudents(Array.isArray(stuData)  ? stuData.filter(s => (s.status||'').toLowerCase() === 'activo') : []);
      } catch (err) { console.error('POS load error:', err); }
    };
    load();
  }, []);

  // FIX: usar calcDisc para el total
  useEffect(() => {
    const t = selectedProducts.reduce((sum, item) => {
      return sum + Math.max(0, Number(item.price) * item.quantity - calcDisc(item));
    }, 0);
    setTotal(t);
  }, [selectedProducts]);

  const categories = [...new Set(products.map(p => p.category_name || p.category).filter(Boolean))];

  const getStudentName = id => {
    const s = students.find(x => String(x.id) === String(id));
    return s?.studentName ?? s?.name ?? '—';
  };

  const handleProductSelectForAdd = productId => {
    const product = products.find(p => p.id === productId);
    setSelectedProductForAdd(product || null);
    setSelectedColor(''); setSelectedSize(''); setQuantity(1);
  };

  const handleAddProductToCart = () => {
    if (!selectedProductForAdd) return;
    if (selectedProductForAdd.type === 'physical') {
      if (selectedProductForAdd.colors?.length && !selectedColor) { alert('Selecciona un color.'); return; }
      if (selectedProductForAdd.sizes?.length  && !selectedSize)  { alert('Selecciona una talla.'); return; }
    }
    setSelectedProducts(prev => [...prev, {
      ...selectedProductForAdd, quantity, selectedColor, selectedSize,
      uniqueCartId: generateUniqueId(), discount: 0, discountType: '%',
    }]);
    setSelectedProductForAdd(null); setQuantity(1);
  };

  const handleRemoveProduct = id =>
    setSelectedProducts(prev => prev.filter(i => i.uniqueCartId !== id));

  const handleDiscountChange = (id, value) => {
    setSelectedProducts(prev => prev.map(it => {
      if (it.uniqueCartId !== id) return it;
      const max   = it.discountType === '%' ? 100 : Number(it.price) * it.quantity;
      const clean = Math.min(max, Math.max(0, Number(value || 0)));
      return { ...it, discount: clean };
    }));
  };

  const handleDiscountTypeChange = (id, type) => {
    setSelectedProducts(prev => prev.map(it =>
      it.uniqueCartId === id ? { ...it, discountType: type, discount: 0 } : it
    ));
  };

  // ── Comprobante ─────────────────────────────────────────────────────────────
  const buildReceiptHTML = ({ folio, fecha, hora, studentName, items, subtotal, total, discountTotal }) => {
    const itemsRows = items.map(it => {
      const opts = [it.color ? `Color: ${it.color}` : '', it.size ? `Talla: ${it.size}` : ''].filter(Boolean).join(' · ');
      const line = Number(it.price) * it.quantity;
      const isP  = (it.discountType || '%') === '%';
      const disc = isP
        ? line * (Math.min(100, Math.max(0, Number(it.discount || 0))) / 100)
        : Math.min(line, Math.max(0, Number(it.discount || 0)));
      const net  = Math.max(0, line - disc);
      const discLabel = disc > 0 ? (isP ? `${Number(it.discount).toFixed(1)}%` : mxn(disc)) : '';
      return `<tr>
        <td>
          <div class="prod-name">${it.name}</div>
          ${opts ? `<div class="prod-meta">${opts}</div>` : ''}
          ${disc > 0 ? `<span class="disc-tag">Descuento ${discLabel} · -${mxn(disc)}</span>` : ''}
        </td>
        <td class="right" style="font-size:13px;color:#374151">${it.quantity}</td>
        <td class="right" style="font-size:13px;color:#374151">${mxn(it.price)}</td>
        <td class="right" style="font-size:13px;font-weight:600;color:#111827">${mxn(net)}</td>
      </tr>`;
    }).join('');

    return `<!doctype html>
<html lang="es"><head><meta charset="utf-8"/>
<title>Comprobante de Venta ${folio}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Inter',system-ui,sans-serif;background:#f3f4f6;display:flex;justify-content:center;padding:40px 16px}
  .card{background:white;width:100%;max-width:580px;border-radius:20px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,.12)}
  .header{background:linear-gradient(135deg,#961914,#c0392b);padding:32px;color:white}
  .header-top{display:flex;justify-content:space-between;align-items:flex-start}
  .brand h1{font-size:20px;font-weight:700;letter-spacing:-.5px}
  .brand p{font-size:12px;opacity:.75;margin-top:3px}
  .folio{background:rgba(255,255,255,.15);border-radius:10px;padding:8px 14px;text-align:right}
  .folio .num{font-size:18px;font-weight:700}.folio .lbl{font-size:11px;opacity:.7}
  .client{margin-top:20px;padding-top:20px;border-top:1px solid rgba(255,255,255,.2)}
  .client .lbl{font-size:11px;opacity:.7;text-transform:uppercase;letter-spacing:.5px}
  .client .name{font-size:18px;font-weight:600;margin-top:4px}
  .body{padding:24px 32px}
  .section-title{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;color:#9ca3af;margin-bottom:12px}
  table{width:100%;border-collapse:collapse}
  th{text-align:left;font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.3px;padding:0 0 8px;border-bottom:2px solid #f3f4f6}
  th.right,td.right{text-align:right}
  td{padding:12px 0;border-bottom:1px solid #f9fafb;vertical-align:top}
  .prod-name{font-size:13px;font-weight:600;color:#111827}
  .prod-meta{font-size:11px;color:#9ca3af;margin-top:2px}
  .disc-tag{display:inline-block;background:#fef2f2;color:#961914;font-size:10px;padding:2px 6px;border-radius:4px;margin-top:3px;font-weight:500}
  .totals{margin-top:16px;background:#f9fafb;border-radius:12px;padding:16px}
  .total-row{display:flex;justify-content:space-between;font-size:13px;color:#6b7280;padding:4px 0}
  .total-final{display:flex;justify-content:space-between;align-items:center;margin-top:12px;padding-top:12px;border-top:2px solid #e5e7eb}
  .total-final .lbl{font-size:15px;font-weight:700;color:#111827}
  .total-final .val{font-size:22px;font-weight:800;color:#961914}
  .footer{padding:20px 32px;background:#fafafa;border-top:1px solid #f3f4f6}
  .footer p{font-size:11px;color:#9ca3af;line-height:1.7}
  .actions{padding:20px 32px 28px;display:flex;gap:10px}
  button{flex:1;padding:12px;border-radius:10px;border:none;cursor:pointer;font-size:14px;font-weight:600}
  button:hover{opacity:.85}
  .btn-primary{background:#961914;color:white}.btn-secondary{background:#f3f4f6;color:#374151}
  @media print{.actions{display:none}body{background:white;padding:0}.card{box-shadow:none;border-radius:0}}
</style></head><body>
<div class="card">
  <div class="header">
    <div class="header-top">
      <div class="brand"><h1>Punto de Venta</h1><p>Comprobante de venta</p></div>
      <div class="folio"><div class="lbl">Folio</div><div class="num">${folio}</div></div>
    </div>
    <div class="client">
      <div class="lbl">Cliente</div>
      <div class="name">${studentName}</div>
      <div style="font-size:12px;opacity:.7;margin-top:6px">${fecha} · ${hora}</div>
    </div>
  </div>
  <div class="body">
    <div class="section-title">Detalle de productos</div>
    <table>
      <thead><tr><th>Producto</th><th class="right">Cant.</th><th class="right">Precio</th><th class="right">Importe</th></tr></thead>
      <tbody>${itemsRows}</tbody>
    </table>
    <div class="totals">
      <div class="total-row"><span>Subtotal</span><span>${mxn(subtotal)}</span></div>
      ${discountTotal > 0 ? `<div class="total-row"><span>Descuentos</span><span style="color:#961914">-${mxn(discountTotal)}</span></div>` : ''}
      <div class="total-final"><span class="lbl">Total</span><span class="val">${mxn(total)}</span></div>
    </div>
  </div>
  <div class="footer"><p>Este comprobante es un documento informativo. Para factura fiscal, solicítela en recepción.</p></div>
  <div class="actions">
    <button class="btn-primary" onclick="window.print()">🖨️ Imprimir / PDF</button>
  </div>
</div>
<script>
  var isWebView = /wv|WebView|Android/i.test(navigator.userAgent);
  if (!isWebView) setTimeout(function(){try{window.print()}catch(e){}}, 600);
</script>
</body></html>`;
  };

  const openReceiptWindow = html => {
    const win = window.open('', '_blank');
    if (!win) return false;
    win.document.open(); win.document.write(html); win.document.close();
    return true;
  };

  const showReceipt = ({ serverResult, cartSnapshot, studentName }) => {
    const now   = new Date();
    const fecha = now.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
    const hora  = now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    const saleId = serverResult?.sale_id || serverResult?.id;
    const folio  = saleId ? `POS-${String(saleId).padStart(6,'0')}` : `POS-${Date.now()}`;

    // FIX: incluir discountType en los items del comprobante
    const items = cartSnapshot.map(p => ({
      name:         p.name,
      price:        Number(p.price),
      quantity:     p.quantity,
      color:        p.selectedColor,
      size:         p.selectedSize,
      discount:     Number(p.discount || 0),
      discountType: p.discountType || '%',
    }));

    const subtotal      = items.reduce((s, it) => s + it.price * it.quantity, 0);
    const discountTotal = items.reduce((s, it) => s + calcDisc(it), 0);
    const totalFinal    = typeof serverResult?.total === 'number'
      ? serverResult.total
      : Math.max(0, subtotal - discountTotal);

    const html   = buildReceiptHTML({ folio, fecha, hora, studentName, items, subtotal, total: totalFinal, discountTotal });
    const opened = openReceiptWindow(html);
    if (!opened) {
      setLastReceiptData({ folio, fecha, hora, studentName, items, subtotal, total: totalFinal, discountTotal });
      alert('Comprobante listo. Tu navegador bloqueó la ventana — usa el botón "Abrir comprobante".');
    } else {
      setLastReceiptData(null);
    }
  };

  const handleProcessSale = async () => {
    if (!selectedStudentId)            { alert('Selecciona una estudiante.'); return; }
    if (selectedProducts.length === 0) { alert('El carrito está vacío.');     return; }

    setProcessing(true);
    const cartSnapshot   = [...selectedProducts];
    const studentNameTxt = getStudentName(selectedStudentId);

    const payload = {
      student_id: selectedStudentId,
      products: cartSnapshot.map(p => {
        const line    = Number(p.price) * p.quantity;
        const disc    = calcDisc(p);
        const pct     = p.discountType === '%' ? Math.min(100, Math.max(0, Number(p.discount || 0))) : 0;
        return {
          id:              p.id,
          name:            p.name,
          price:           Number(p.price),
          quantity:        p.quantity,
          type:            p.type,
          color:           p.selectedColor,
          size:            p.selectedSize,
          imageUrl:        p.imageUrl,
          discountPercent: pct,
          discount:        disc,
        };
      }),
      total,
    };

    try {
      const res    = await fetch('/api/register_sale.php', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message || 'Error al guardar la venta.');

      // Registrar pago si hay disciplina
      const disciplineTotal = payload.products
        .filter(p => p.type === 'discipline')
        .reduce((sum, p) => sum + Math.max(0, p.price * p.quantity - (p.discount || 0)), 0);

      if (disciplineTotal > 0) {
        const now = new Date();
        await fetch('/api/create_or_update_payment.php', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            studentId: selectedStudentId,
            month:  String(now.getMonth() + 1).padStart(2, '0'),
            year:   String(now.getFullYear()),
            amount: disciplineTotal,
            status: 'paid',
          }),
        });
      }

      showReceipt({ serverResult: result, cartSnapshot, studentName: studentNameTxt });
      setSelectedStudentId(''); setSelectedProducts([]); setTotal(0);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const visibleProducts = categoryFilter
    ? products.filter(p => (p.category_name || p.category) === categoryFilter)
    : products;

  return (
    <SharedCard title="Punto de Venta">
      <div className="flex flex-col lg:flex-row gap-6">

        {/* ── Catálogo ── */}
        <div className="lg:w-3/5 flex flex-col gap-4">
          <SharedSelect
            label="Estudiante"
            value={selectedStudentId}
            onChange={e => setSelectedStudentId(Number(e.target.value))}
            options={[
              { value: '', label: 'Selecciona una estudiante' },
              ...students.map(s => ({ value: s.id, label: getStudentName(s.id) }))
            ]}
          />

          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setCategoryFilter('')}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${!categoryFilter ? 'bg-[#961914] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              Todos
            </button>
            {categories.map(cat => (
              <button key={cat} onClick={() => setCategoryFilter(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${categoryFilter === cat ? 'bg-[#961914] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[520px] overflow-y-auto pr-1">
            {visibleProducts.map(product => (
              <div key={product.id} onClick={() => handleProductSelectForAdd(product.id)}
                className={`bg-white border-2 rounded-xl p-3 cursor-pointer transition-all shadow-sm hover:shadow-md ${
                  selectedProductForAdd?.id === product.id ? 'border-[#961914] ring-2 ring-[#961914]/20' : 'border-gray-100 hover:border-gray-300'
                }`}>
                {product.imageUrl
                  ? <img src={product.imageUrl} alt={product.name} className="h-20 w-full object-cover rounded-lg mb-2" />
                  : <div className="h-20 w-full bg-gray-50 rounded-lg mb-2 flex items-center justify-center text-gray-300 text-2xl">🛍</div>
                }
                <p className="text-sm font-semibold text-gray-900 leading-tight">{product.name}</p>
                <p className="text-xs text-[#961914] font-bold mt-1">{mxn(product.price)}</p>
                <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full font-medium ${
                  product.type === 'physical'   ? 'bg-blue-50 text-blue-600' :
                  product.type === 'discipline' ? 'bg-purple-50 text-purple-600' :
                  'bg-green-50 text-green-600'
                }`}>
                  {product.type === 'physical' ? 'Físico' : product.type === 'discipline' ? 'Disciplina' : 'Servicio'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Carrito ── */}
        <div className="lg:w-2/5 flex flex-col gap-4">

          {selectedProductForAdd && (
            <div className="bg-white border-2 border-[#961914]/30 rounded-xl p-4 shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-semibold text-gray-900">{selectedProductForAdd.name}</p>
                  <p className="text-[#961914] font-bold text-sm">{mxn(selectedProductForAdd.price)}</p>
                </div>
                <button onClick={() => setSelectedProductForAdd(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
              </div>
              {selectedProductForAdd.type === 'physical' && selectedProductForAdd.colors?.length > 0 && (
                <SharedSelect label="Color" value={selectedColor} onChange={e => setSelectedColor(e.target.value)}
                  options={[{ value: '', label: 'Selecciona' }, ...selectedProductForAdd.colors.map(c => ({ value: c, label: c }))]} />
              )}
              {selectedProductForAdd.type === 'physical' && selectedProductForAdd.sizes?.length > 0 && (
                <SharedSelect label="Talla" value={selectedSize} onChange={e => setSelectedSize(e.target.value)}
                  options={[{ value: '', label: 'Selecciona' }, ...selectedProductForAdd.sizes.map(s => ({ value: s, label: s }))]} />
              )}
              <SharedInput label="Cantidad" type="number" value={quantity}
                onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} min="1" />
              <SharedButton onClick={handleAddProductToCart} className="w-full mt-2">Agregar al carrito</SharedButton>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col flex-1">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Carrito</h3>
              <p className="text-xs text-gray-400">{selectedProducts.length} producto(s)</p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-64">
              {selectedProducts.length === 0 ? (
                <div className="text-center py-8 text-gray-300">
                  <div className="text-4xl mb-2">🛒</div>
                  <p className="text-sm">El carrito está vacío</p>
                </div>
              ) : selectedProducts.map(item => {
                const disc = calcDisc(item);
                const net  = Math.max(0, Number(item.price) * item.quantity - disc);
                return (
                  <div key={item.uniqueCartId} className="flex gap-3 bg-gray-50 rounded-lg p-3">
                    {item.imageUrl && <img src={item.imageUrl} alt={item.name} className="h-10 w-10 object-cover rounded-lg flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{item.name} ×{item.quantity}</p>
                      {(item.selectedColor || item.selectedSize) && (
                        <p className="text-xs text-gray-400">{[item.selectedColor, item.selectedSize].filter(Boolean).join(' · ')}</p>
                      )}
                      <p className="text-xs font-bold text-[#961914] mt-0.5">{mxn(net)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <button onClick={() => handleRemoveProduct(item.uniqueCartId)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          max={item.discountType === '%' ? 100 : Number(item.price) * item.quantity}
                          step={item.discountType === '%' ? 1 : 0.01}
                          value={item.discount ?? 0}
                          onChange={e => handleDiscountChange(item.uniqueCartId, e.target.value)}
                          className="w-16 text-xs text-right border border-gray-200 rounded px-1 py-0.5 bg-white"
                          placeholder={item.discountType === '%' ? 'Desc%' : 'Desc$'}
                        />
                        <button
                          onClick={() => handleDiscountTypeChange(item.uniqueCartId, item.discountType === '%' ? '$' : '%')}
                          className="text-xs font-bold px-1.5 py-0.5 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors"
                          title="Cambiar tipo de descuento"
                        >
                          {item.discountType || '%'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-gray-600 font-medium">Total</span>
                <span className="text-2xl font-extrabold text-[#961914]">{mxn(total)}</span>
              </div>
              <SharedButton onClick={handleProcessSale} className="w-full" disabled={processing}>
                {processing ? 'Procesando…' : 'Procesar Venta'}
              </SharedButton>
              {lastReceiptData && (
                <button onClick={() => {
                  const html = buildReceiptHTML(lastReceiptData);
                  const opened = openReceiptWindow(html);
                  if (opened) setLastReceiptData(null);
                }} className="mt-2 w-full text-sm text-[#961914] underline">
                  Abrir comprobante bloqueado
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </SharedCard>
  );
};

export default POSComponent;